import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Link,
  Typography,
  Stack,
  IconButton,
  FormHelperText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { FormLink } from "../RecordDetail/ApplicationData";
import Grid from "@mui/material/Grid";


// URLの形式チェック（http/httpsで始まるURLか検証する）
const isValidUrl = (text: string) =>
  /^https?:\/\/[\w/:%#\$&\?\(\)~\.=\+\-]+/.test(text);

/**
 * リンク入力項目コンポーネント（1件分）
 * - 説明とURLの入力欄を表示し、URLは有効な場合クリック可能なリンクとして表示する
 * - props: data (リンクデータ), onChange (変更時コールバック), onDelete (削除時コールバック)
 */
const LinkItem: React.FC<{
  data: FormLink;
  onChange: (newData: FormLink) => void;
  onDelete: () => void;
}> = ({ data, onChange, onDelete }) => {
  const [editingUrl, setEditingUrl] = useState(true);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleUrlConfirm = () => {
    setEditingUrl(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      handleUrlConfirm();
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Stack spacing={1} sx={{ flexGrow: 1 }}>
        {/* 説明欄 */}
        <TextField
          fullWidth
          label="説明"
          value={data.description}
          onChange={(e) =>
            onChange({ ...data, description: e.target.value })
          }
          variant="outlined"
          placeholder="例：会社のWebサイト"
        />

        {/* リンク欄 ＋ ✖︎ 削除ボタン */}
        <Box
          onClick={() => setEditingUrl(true)}
        >
          {editingUrl ? (
            <TextField
              fullWidth
              label="リンク"
              value={data.url}
              inputRef={inputRef}
              onChange={(e) => onChange({ ...data, url: e.target.value })}
              onBlur={handleUrlConfirm}
              onKeyDown={handleKeyDown}
              placeholder="https://example.com"
              variant="outlined"
            />
          ) : (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                border: "1px solid rgba(0, 0, 0, 0.23)",
                borderRadius: "4px",
                backgroundColor: "#f9f9f9",
                cursor: "text",
              }}
            >
              {isValidUrl(data.url) ? (
                <Link
                  href={data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {data.url}
                </Link>
              ) : (
                <Typography color="text.secondary">
                  {data.url || "リンク未入力"}
                </Typography>
              )}
            </Box>
          )}
        </Box>

      </Stack>
        <IconButton
          onClick={onDelete}
          size="large"
          aria-label="削除"
        >
          <CloseIcon />
        </IconButton>
    </Box>
  );
};

/**
 * リンク一覧入力コンポーネント
 * - 複数のリンク（説明＋URL）を入力・追加・削除できる
 * - props: label (フィールドラベル), value (初期リンクリスト), onChange (変更時コールバック)
 */
export const FormLinkList = ({
  label,
  value,
  isRequired,
  onChange,
  errorMessage,
  hasError,
}: {
  label: string;
  value?: FormLink[];
  isRequired?: boolean;
  onChange: (newLinks: FormLink[]) => void;
  errorMessage?: string;
  hasError?: boolean;
}) => {
  const [links, setLinks] = useState<FormLink[]>(value ?? []);

  useEffect(() => {
    onChange(links);
  }, [links]);

  // 指定インデックスのリンクデータを更新する
  const handleChange = (index: number, newData: FormLink) => {
    const updated = [...links];
    updated[index] = newData;
    setLinks(updated);
  };

  // 新しい空のリンク項目を追加する
  const handleAdd = () => {
    setLinks((prev) => [...prev, { id: "", description: "", url: "", order: 0 }]);
  };

  return (
    <Box>
      <Typography 
        variant="h6" 
        gutterBottom
        sx={(theme) => ({
          color: hasError ? theme.palette.error.main : "inherit",
          display: "inline-flex",
          alignItems: "center",
        })}
      >
        {label ?? "リンク入力（説明＋URL）"}
        {isRequired && (
          <Box 
            component="span" 
            sx={(theme) => ({
              ml: 0.5,
              color: hasError ? theme.palette.error.main : "inherit",
            })}
          >
            *
          </Box>
        )}
      </Typography>

      {/* エラーメッセージ */}
      {errorMessage && (
        <FormHelperText error sx={{ mb: 1 }}>
          {errorMessage}
        </FormHelperText>
      )}


      <Stack spacing={2}>
        {links.map((link, index) => (
          <LinkItem
            key={index}
            data={link}
            onChange={(newData) => handleChange(index, newData)}
            onDelete={() => {
              const updated = [...links];
              updated.splice(index, 1);
              setLinks(updated);
            }}
          />
        ))}

      </Stack>
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <IconButton onClick={handleAdd} color="primary" size="large">
          <AddIcon />
        </IconButton>
      </Box>

    </Box>
  );
};

export default FormLinkList;
